/**
 * AddressAutocomplete
 *
 * Reusable, stateless address autocomplete component for the VERC CRM.
 *
 * Design rules (from implementation plan):
 * - Minimum 3 characters before any API call
 * - 300ms debounce on keystroke
 * - One AutocompleteSessionToken per focus→selection cycle (useRef, not useState)
 * - Predictions are fetched; Place Details (fetchFields) only called on explicit selection
 * - 60-second prediction cache (module-level Map) — prevents duplicate API calls for same input
 * - autoComplete="new-password" on input to suppress native browser autofill
 * - Keyboard: ↑↓ navigate, Enter selects, Escape closes, Tab exits naturally
 * - Falls back to plain <Input> if script not loaded or API key not set
 * - Predictions are restricted to the tenant's country (`regionCode` prop, else
 *   the company locale) — fewer irrelevant suggestions, fewer paid requests
 * - Session token is refreshed after each successful selection
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useGooglePlacesScript } from '@/hooks/useGooglePlacesScript';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { parseGoogleAddress, NormalizedAddress } from '@/utils/parseGoogleAddress';
import { MapPin, Loader2 } from 'lucide-react';

// ─── Prediction cache ─────────────────────────────────────────────────────────
// Module-level: survives component remounts, shared across all instances
interface CacheEntry {
  suggestions: google.maps.places.AutocompleteSuggestion[];
  expiresAt: number;
}
const predictionCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000; // 60 seconds

function getCachedPredictions(key: string): google.maps.places.AutocompleteSuggestion[] | null {
  const entry = predictionCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    predictionCache.delete(key);
    return null;
  }
  return entry.suggestions;
}

function setCachedPredictions(
  key: string,
  suggestions: google.maps.places.AutocompleteSuggestion[]
) {
  predictionCache.set(key, { suggestions, expiresAt: Date.now() + CACHE_TTL_MS });
}

// ─── Types ────────────────────────────────────────────────────────────────────
export interface AddressAutocompleteProps {
  value: string;
  onChange: (rawValue: string) => void;
  onAddressSelected: (normalized: NormalizedAddress) => void;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  /** ISO 3166-1 alpha-2 region to restrict predictions to; defaults to the tenant's country. */
  regionCode?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelected,
  disabled = false,
  readOnly = false,
  placeholder = 'Start typing an address…',
  className,
  id,
  regionCode,
}: AddressAutocompleteProps) {
  const { isReady } = useGooglePlacesScript();
  const { locale } = useCompanyLocale();
  const region = (regionCode ?? locale.placesRegion).toLowerCase();
  const addressLabels = locale.addressLabels;

  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [partialWarning, setPartialWarning] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Session token — one per focus→selection cycle
  // useRef so it never triggers re-renders and persists across renders
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Debounce timer
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track latest request to avoid race conditions
  const requestIdRef = useRef(0);

  // Container ref for click-outside detection
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Session token helpers ──────────────────────────────────────────────────
  const getOrCreateToken = useCallback(() => {
    if (!window.google?.maps?.places) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const refreshToken = useCallback(() => {
    if (!window.google?.maps?.places) return;
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }, []);

  // ── Fetch predictions ─────────────────────────────────────────────────────
  const fetchPredictions = useCallback(
    async (input: string) => {
      if (!window.google?.maps?.places) return;
      if (input.length < 3) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      const cacheKey = `${region}:${input.toLowerCase().trim()}`;
      const cached = getCachedPredictions(cacheKey);
      if (cached) {
        setSuggestions(cached);
        setIsOpen(cached.length > 0);
        return;
      }

      const requestId = ++requestIdRef.current;
      setIsLoadingPredictions(true);

      try {
        const { AutocompleteSuggestion } = await window.google.maps.importLibrary(
          'places'
        ) as google.maps.PlacesLibrary;

        const token = getOrCreateToken();
        const request: google.maps.places.AutocompleteRequest = {
          input,
          includedRegionCodes: [region], // tenant's country only
          ...(token ? { sessionToken: token } : {}),
        };

        const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        // Discard stale response if a newer request has fired
        if (requestId !== requestIdRef.current) return;

        setCachedPredictions(cacheKey, results);
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setHighlightedIndex(-1);
      } catch {
        // Predictions fail silently — user can continue typing or use manual entry
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoadingPredictions(false);
        }
      }
    },
    [getOrCreateToken, region]
  );

  // ── Input handler (debounced) ─────────────────────────────────────────────
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue); // keep RHF value in sync on every keystroke
      setDetailsError(false);
      setPartialWarning(false);

      if (!isReady) return;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (newValue.length < 3) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(newValue);
      }, 300);
    },
    [onChange, isReady, fetchPredictions]
  );

  // ── Selection handler ─────────────────────────────────────────────────────
  const handleSelect = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      const placePrediction = suggestion.placePrediction;
      if (!placePrediction) return;

      // Close dropdown immediately for responsiveness
      setIsOpen(false);
      setSuggestions([]);
      setIsLoadingDetails(true);
      setDetailsError(false);
      setPartialWarning(false);

      // Update the input value to the selected text right away
      const selectedText = placePrediction.text?.toString() ?? '';
      onChange(selectedText);

      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress', 'location'],
        });
        // fetchFields() automatically ends the autocomplete session

        const normalized = parseGoogleAddress(place);

        onAddressSelected(normalized);

        if (normalized.isPartial) {
          setPartialWarning(true);
        }
      } catch {
        // getDetails() failed — keep typed text, show error message
        // onAddressSelected is NOT called, so split fields are untouched
        setDetailsError(true);
      } finally {
        setIsLoadingDetails(false);
        // Refresh token for the next search session
        refreshToken();
      }
    },
    [onChange, onAddressSelected, refreshToken]
  );

  // ── Focus: create session token ───────────────────────────────────────────
  const handleFocus = useCallback(() => {
    if (!isReady) return;
    getOrCreateToken();
  }, [isReady, getOrCreateToken]);

  // ── Keyboard navigation ───────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
            handleSelect(suggestions[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        case 'Tab':
          // Tab closes dropdown but does not select — natural form navigation
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    },
    [isOpen, suggestions, highlightedIndex, handleSelect]
  );

  // ── Click outside ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Cleanup debounce on unmount ───────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  const isDisabled = disabled || readOnly;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={placeholder}
          className={cn('pr-8', className)}
          // Suppress native browser autofill — this is the reliable cross-browser approach
          autoComplete="new-password"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-owns={isOpen ? 'address-autocomplete-listbox' : undefined}
          aria-activedescendant={
            highlightedIndex >= 0 ? `address-option-${highlightedIndex}` : undefined
          }
          role="combobox"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          {isLoadingPredictions || isLoadingDetails ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isReady ? (
            <MapPin className="h-4 w-4 opacity-40" />
          ) : null}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul
          id="address-autocomplete-listbox"
          role="listbox"
          aria-label="Address suggestions"
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md text-sm overflow-hidden"
        >
          {suggestions.map((suggestion, index) => {
            const text = suggestion.placePrediction?.text?.toString() ?? '';
            const mainText = suggestion.placePrediction?.mainText?.toString() ?? text;
            const secondaryText = suggestion.placePrediction?.secondaryText?.toString() ?? '';

            return (
              <li
                key={index}
                id={`address-option-${index}`}
                role="option"
                aria-selected={index === highlightedIndex}
                className={cn(
                  'flex items-start gap-2 px-3 py-2 cursor-pointer select-none',
                  index === highlightedIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
                onMouseDown={(e) => {
                  // Prevent onBlur firing before onClick
                  e.preventDefault();
                  handleSelect(suggestion);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium truncate">{mainText}</span>
                  {secondaryText && (
                    <span className="text-muted-foreground text-xs truncate">{secondaryText}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Inline feedback messages */}
      {detailsError && (
        <p className="mt-1 text-xs text-warning">
          Address details could not be loaded — please fill {addressLabels.locality.toLowerCase()}/{addressLabels.region.toLowerCase()}/{addressLabels.postcode.toLowerCase()} manually.
        </p>
      )}
      {partialWarning && !detailsError && (
        <p className="mt-1 text-xs text-warning">
          Address selected — please verify {addressLabels.locality.toLowerCase()}/{addressLabels.region.toLowerCase()}/{addressLabels.postcode.toLowerCase()}.
        </p>
      )}
    </div>
  );
}
